import { Logger, AnyFunc, createEventEmitter, EventEmitter, Error } from '@quickplay/foundation';
import {
  DrmScheme,
  MediaType,
  PlaybackStatistics,
  RequestType,
  SeekRangeInfo,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackType,
  TrackVariantInfo,
  PlaybackOptions,
} from '@quickplay/player-interface';

import { PlayerAdapter } from '../../PlayerAdapter';
import { convertDashJSMessageToEmsg } from './Utils';
import { convertToPlayerError } from './ErrorHandler';
import SceUvp from './SceUvp';
import Dash from './dashjs';

const AUTH_TOKEN_TYPE = 'authToken';
const AUTH_TOKEN_TYPE_VALUE = 'query';
const MAX_VALUE = 2147483647;

const YOSPACE_SCTE_EVENT = 'urn:yospace:a:id3:2016';
const BUFFER_EMPTY_EVENT = 'bufferStalled';
const BUFFER_LOADED_EVENT = 'bufferLoaded';
const UVP_PLAYER_ADAPTER_ERROR = '[UVPPlayerAdapter] e';

// UVP Timeupdate event fires 250 to 300 ms Aprox, based on that we have given below timeout
const END_OF_STREAM_DETECT_TIMEOUT_IN_MS = 500;
const END_OF_STEAM_THERSHOLD_IN_SEC = 0.5;

export class UVPPlayerAdapter implements PlayerAdapter {
  private emitter_: EventEmitter;
  private uvpPlayer_: SceUvp.Player;
  private sceUvp_: typeof SceUvp;
  private allTracksInfo_: TrackInfo[] = [];
  private activeTracks_: TrackInfo[] = [];
  private isBuffering_ = false;
  private isDashPlayerSetup_ = false;
  // Stream End Timer for to get Precise stream end
  private endOfStreamTimer_: any;

  private requestInterceptor_: ((requestType: RequestType, request: any) => void) | undefined;

  constructor(
    private sceUvpDep_: typeof SceUvp,
    private rootElement_: HTMLElement,
    private logger_: Logger,
    private contentUrl_: string,
    private playbackOptions_: PlaybackOptions,
    private licenseUrl_?: string,
    private licenseCustomData_?: string,
    private drmScheme_?: DrmScheme,
    private mediaType_?: MediaType,
  ) {
    this.sceUvp_ = this.sceUvpDep_;
    this.emitter_ = createEventEmitter();

    this.logger_.debug('[UVPPlayerAdapter] DRM', this.licenseUrl_, this.licenseCustomData_, this.drmScheme_);

    this.sceUvp_.use('dash', () => this.mediaType_ === MediaType.DASH);
    this.sceUvp_.use('html5');

    this.uvpPlayer_ = new this.sceUvp_.Player(this.rootElement_, {
      type: this.mediaType_,
      controls: false,
      showControls: false,
      autoplay: this.playbackOptions_.autoPlayOnLoad,
      fit: 'contain' as SceUvp.Fit.contain,
    });

    if (this.playbackOptions_.bufferingTimeout) {
      this.uvpPlayer_.timeout = this.playbackOptions_.bufferingTimeout * 1000;
    } else {
      // Note: UVP player relies on 'waiting' and 'ended' events from video element
      // to detemine if the player is buffering and fires a timeout error if exceeding 30s (default).
      // But this is not an accurate way to determine 'buffering' when using dashjs player.
      // Hence, we need this work-around to opt-out of buffering timeout error from UVP player.
      // dashjs player would fail when its unable to fetch a segments and fulfill playback.
      this.uvpPlayer_.timeout = MAX_VALUE;
    }

    this.setupUvpPlayer_();
  }

  private getTracks_(subtitles: SceUvp.TrackInfo[], audios: SceUvp.TrackInfo[]): TrackInfo[] {
    let tracks = [] as TrackInfo[];
    if (subtitles && audios) {
      const subtitlesTracks = subtitles.map(this.uvpTrackToTrackInfoMapper_(TrackType.TEXT));
      const audiosTracks = audios.map(this.uvpTrackToTrackInfoMapper_(TrackType.AUDIO));
      tracks = subtitlesTracks.concat(audiosTracks);
    }
    return tracks;
  }

  private uvpTrackToTrackInfoMapper_(type: TrackType) {
    return (track: SceUvp.TrackInfo): TrackInfo =>
      ({
        type: type,
        language: track ? track.lang : 'default',
      }) as TrackInfo;
  }

  private changeIsBuffering_(buffering: boolean): void {
    if (buffering !== this.isBuffering_) {
      this.isBuffering_ = buffering;
      this.onBuffering_();
    }
  }

  private onDashBufferingStartEvent_ = (): void => {
    this.changeIsBuffering_(true);
  };

  private onDashBufferingEndEvent_ = (): void => {
    this.changeIsBuffering_(false);
  };

  private onDashEmsgEvent_ = (evt: any): void => {
    this.onEmsgEvent_(evt);
  };

  private setupDashPlayer_(): void {
    const dashPlayer = this.dashPlayer_();
    if (!dashPlayer || this.isDashPlayerSetup_) {
      return;
    }

    // Dev Note: The latest UVP player seems to always enable captions.
    // We need this to opt-out of automatic caption selection
    this.uvpPlayer_.subtitles = false;

    this.logger_.log('[DASH JS] adding listeners');
    dashPlayer.on(YOSPACE_SCTE_EVENT, this.onDashEmsgEvent_);
    dashPlayer.on(BUFFER_EMPTY_EVENT, this.onDashBufferingStartEvent_);
    dashPlayer.on(BUFFER_LOADED_EVENT, this.onDashBufferingEndEvent_);

    this.isDashPlayerSetup_ = true;
  }

  private teardownDashPlayer_(): void {
    const dashPlayer = this.dashPlayer_();
    if (!dashPlayer) {
      return;
    }

    dashPlayer.off(YOSPACE_SCTE_EVENT, this.onDashEmsgEvent_);
    dashPlayer.off(BUFFER_EMPTY_EVENT, this.onDashBufferingStartEvent_);
    dashPlayer.off(BUFFER_LOADED_EVENT, this.onDashBufferingEndEvent_);
  }

  private onEmsgEvent_ = (evt: any): void => {
    const data = Object.assign({ type: 'emsg' }, convertDashJSMessageToEmsg(evt));
    this.emitter_.publish('timedmetadata', data);
  };

  private onUVPReady_: () => void = () => {
    this.changeIsBuffering_(false);
  };

  private onUVPLoadStart_: () => void = () => {
    this.changeIsBuffering_(true);
  };

  private onUVPLoadedMetadata_: () => void = () => {
    this.changeIsBuffering_(false);
    this.updateTracks_();

    this.emitter_.publish('trackavailabilitychanged');
  };

  private onUVPLoadedData_: () => void = () => {
    this.changeIsBuffering_(false);
    this.updateTracks_();
    this.emitter_.publish('trackschanged');
  };

  private onUVPCanPlay_: () => void = () => {
    this.changeIsBuffering_(false);
  };

  private onUVPEncrypted_: () => void = () => {
    // this.logger_.debug('[UVPPlayerAdapter Event] - encrypted', evt);
  };

  private onUVPTimeUpdate_: () => void = () => {
    // // this.logger_.debug('[UVPPlayerAdapter Event] - timeupdate', evoht);
    // Note: We should present the play-head position in epochtime when playing Live.
    // UVP does not expose an API for this currently, hence resorting to an un-documented API
    // to extract the information.
    const player = this.uvpPlayer_;
    const dashPlayer = this.dashPlayer_();
    const curTime = dashPlayer ? dashPlayer.getVideoElement().currentTime : 0;
    const position = this.isLive ? Math.floor(curTime) : player.currentTime;

    this.onTimeUpdate_(position, player.duration);

    if (!this.isLive) {
      // Clear Stream End Timer
      this.clearEndOfStreamTimer_();
      // Stream End Timer
      this.endOfStreamTimer_ = setTimeout(this.onEnded_, END_OF_STREAM_DETECT_TIMEOUT_IN_MS);
    }
  };

  private onUVPPlay_: () => void = () => {
    this.changeIsBuffering_(false);
    this.onPlaying_();
  };

  private onUVPPlaying_: () => void = () => {
    this.changeIsBuffering_(false);
  };

  private onUVPPause_: () => void = () => {
    this.onPause_();
  };

  // private onUVPSuspend_: () => void = () => {
  // this.changeIsBuffering_(true);
  // };

  // private onUVPWaiting_: () => void = () => {
  // this.changeIsBuffering_(true);
  // };

  private onUVPSeeked_: () => void = () => {
    this.onSeeked_();
  };

  // private onUVPEnded_: () => void = () => {
  //   this.onEnded_();
  // };

  private onUVPError_ = (errorEvent: any): void => {
    if (errorEvent && errorEvent.detail) {
      this.onVideoError_(errorEvent.detail);
    }
  };

  private setupUvpPlayer_(): void {
    const player = this.uvpPlayer_;
    this.isBuffering_ = true;

    player.on('ready' as SceUvp.PlayerEvent.ready, this.onUVPReady_);
    player.on('loadstart' as SceUvp.PlayerEvent.loadstart, this.onUVPLoadStart_);
    player.on('loadedmetadata' as SceUvp.PlayerEvent.loadedmetadata, this.onUVPLoadedMetadata_);
    player.on('loadeddata' as SceUvp.PlayerEvent.loadeddata, this.onUVPLoadedData_);
    player.on('canplay' as SceUvp.PlayerEvent.canplay, this.onUVPCanPlay_);
    player.on('encrypted' as SceUvp.PlayerEvent.encrypted, this.onUVPEncrypted_);
    player.on('timeupdate' as SceUvp.PlayerEvent.timeupdate, this.onUVPTimeUpdate_);
    player.on('play' as SceUvp.PlayerEvent.play, this.onUVPPlay_);
    player.on('playing' as SceUvp.PlayerEvent.playing, this.onUVPPlaying_);
    player.on('pause' as SceUvp.PlayerEvent.pause, this.onUVPPause_);
    // player.on('suspend' as SceUvp.PlayerEvent.suspend, this.onUVPSuspend_);
    // player.on('waiting' as SceUvp.PlayerEvent.waiting, this.onUVPWaiting_);
    player.on('seeked' as SceUvp.PlayerEvent.seeked, this.onUVPSeeked_);
    //  player.on('ended' as SceUvp.PlayerEvent.ended, this.onUVPEnded_);
    player.on('error' as SceUvp.PlayerEvent.error, this.onUVPError_);
  }

  private teardownUvpPlayer_(): void {
    const player = this.uvpPlayer_;
    player.off('ready' as SceUvp.PlayerEvent.ready, this.onUVPReady_);
    player.off('loadstart' as SceUvp.PlayerEvent.loadstart, this.onUVPLoadStart_);
    player.off('loadedmetadata' as SceUvp.PlayerEvent.loadedmetadata, this.onUVPLoadedMetadata_);
    player.off('loadeddata' as SceUvp.PlayerEvent.loadeddata, this.onUVPLoadedData_);
    player.off('canplay' as SceUvp.PlayerEvent.canplay, this.onUVPCanPlay_);
    player.off('encrypted' as SceUvp.PlayerEvent.encrypted, this.onUVPEncrypted_);
    player.off('timeupdate' as SceUvp.PlayerEvent.timeupdate, this.onUVPTimeUpdate_);
    player.off('play' as SceUvp.PlayerEvent.play, this.onUVPPlay_);
    player.off('playing' as SceUvp.PlayerEvent.playing, this.onUVPPlaying_);
    player.off('pause' as SceUvp.PlayerEvent.pause, this.onUVPPause_);
    // player.off('suspend' as SceUvp.PlayerEvent.suspend, this.onUVPSuspend_);
    // player.off('waiting' as SceUvp.PlayerEvent.waiting, this.onUVPWaiting_);
    player.off('seeked' as SceUvp.PlayerEvent.seeked, this.onUVPSeeked_);
    //  player.off('ended' as SceUvp.PlayerEvent.ended, this.onUVPEnded_);
    player.off('error' as SceUvp.PlayerEvent.error, this.onUVPError_);

    this.isBuffering_ = false;
    this.teardownDashPlayer_();
  }

  private updateTracks_(): void {
    const player = this.uvpPlayer_;
    const dashPlayer = this.dashPlayer_();
    const hasAudio = dashPlayer && dashPlayer.getCurrentTrackFor('audio');
    this.allTracksInfo_ = this.getTracks_(player.subtitleTracks, player.audioTracks);
    const activeSubtitles = player.subtitles ? [player.subtitleTracks[player.subtitles]] : [];
    const activeAudio = hasAudio && player.audio && player.audioTracks.length ? [player.audioTracks[player.audio]] : [];
    this.activeTracks_ = this.getTracks_(activeSubtitles, activeAudio);
  }

  getBitListInfo = (dashPlayer: Dash.MediaPlayerClass, type: Dash.MediaType): Dash.BitrateInfo => {
    const index = dashPlayer.getQualityFor(type);
    const bitList = dashPlayer.getBitrateInfoListFor(type);
    return bitList[index];
  };

  getBufferRange = (dashPlayer: Dash.MediaPlayerClass, type: Dash.MediaType): string => {
    return this.getBufferedAhead(dashPlayer, type).toString();
  };

  getBufferedAhead(dashPlayer: Dash.MediaPlayerClass, type: Dash.MediaType): number {
    const dashMetrics = dashPlayer.getDashMetrics();
    const mediaType = type === 'video' ? 'video' : 'audio';
    return dashMetrics.getCurrentBufferLevel(mediaType);
  }

  getDroppedFrame = (dashPlayer: Dash.MediaPlayerClass): Dash.IDroppedFrames | number => {
    const dashMetrics = dashPlayer.getDashMetrics();
    const droppedFramesMetrics = dashMetrics.getCurrentDroppedFrames();
    return droppedFramesMetrics ? droppedFramesMetrics.droppedFrames : 0;
  };

  private onSeeked_ = (): void => {
    const player = this.uvpPlayer_;
    this.emitter_.publish('playerseeked', player.currentTime);
    // To clear the stream end timer while seeking
    //   this.clearEndOfStreamTimer_();
  };

  private onEnded_ = (): void => {
    // Fix : FLPS - 61 - Player emits inprogress after streamends
    const player = this.uvpPlayer_;
    const dashPlayer = this.dashPlayer_();
    const curTime = dashPlayer ? dashPlayer.getVideoElement().currentTime : 0;
    const timeDiff = curTime - player.duration;
    if (!this.isLive && END_OF_STEAM_THERSHOLD_IN_SEC > Math.abs(timeDiff)) {
      this.emitter_.publish('playerended');
    }
  };

  private clearEndOfStreamTimer_ = (): void => {
    if (this.endOfStreamTimer_) {
      clearTimeout(this.endOfStreamTimer_);
    }
  };

  private onPlaying_ = (): void => {
    this.emitter_.publish('playerstarted');
  };

  private onPause_ = (): void => {
    this.emitter_.publish('playerpaused');
  };

  private onBuffering_ = (): void => {
    const eventName = this.isBuffering_ ? 'bufferingstart' : 'bufferingend';
    this.emitter_.publish(eventName);
  };

  private onTimeUpdate_ = (position: number, duration: number): void => {
    this.emitter_.publish('timeupdate', position, duration);
  };

  private onVideoError_ = (error: any): void => {
    if (error && error.fatal) {
      const playerError: Error = convertToPlayerError(error);
      this.emitter_.publish('error', playerError);
    } else {
      this.logger_.warn(`An error occurred, but the player is attempting to recover from the error`, error);
    }
  };

  private processRequestInterceptor<T>(type: RequestType, url: T): T {
    if (!this.requestInterceptor_ || !url) {
      return url;
    }

    let updatedUrl = url;
    const request = { uris: [url], [AUTH_TOKEN_TYPE]: AUTH_TOKEN_TYPE_VALUE };
    this.requestInterceptor_(type, request);
    if (request.uris.length > 0) {
      updatedUrl = request.uris[0];
    }

    return updatedUrl;
  }

  private dashPlayer_ = (): Dash.MediaPlayerClass | null => {
    let dashPlayer = null;
    if (this.uvpPlayer_ && this.uvpPlayer_.adapter && this.uvpPlayer_.adapter.player) {
      dashPlayer = this.uvpPlayer_.adapter.player;
    }
    return dashPlayer;
  };

  public get clockSyncUri(): string {
    return '';
  }

  public get minBitrate(): number {
    return 0;
  }

  public set minBitrate(_: number) {
    // no-op
  }

  public get maxBitrate(): number {
    return 0;
  }

  public set maxBitrate(_: number) {
    // no-op
  }

  public get availableBitrates(): number[] {
    return [];
  }

  public get bufferBehind(): number {
    return 0;
  }

  public get internalPlayerName(): string {
    return 'uvp-player';
  }

  public get rawLibrary(): typeof SceUvp {
    return this.sceUvp_;
  }

  public get rawPlayer(): SceUvp.Player | null {
    return this.uvpPlayer_;
  }

  public get internalPlayerVersion(): string {
    return this.uvpPlayer_.version;
  }

  public get maxBuffer(): number {
    // Can"t find
    return 0;
  }

  public get minBuffer(): number {
    // Can"t find
    return 0;
  }

  public get isLive(): boolean {
    const player = this.dashPlayer_();
    try {
      if (player) {
        return player.isDynamic();
      }
    } catch (e) {
      this.logger_.error(e);
    }
    return this.uvpPlayer_.isLive;
  }

  public get playbackOptions(): PlaybackOptions {
    return this.playbackOptions_;
  }

  public get playbackRate(): number {
    const player = this.dashPlayer_();
    if (player) {
      try {
        return player.getPlaybackRate();
      } catch (e) {
        this.logger_.error(UVP_PLAYER_ADAPTER_ERROR, e);
        return 0;
      }
    }
    return 1;
  }

  public get volume(): number {
    const player = this.dashPlayer_();
    if (player) {
      try {
        return player.getVideoElement().volume;
      } catch (e) {
        this.logger_.error(UVP_PLAYER_ADAPTER_ERROR, e);
        return 1;
      }
    }
    return 1;
  }

  public set volume(volumeLevel: number) {
    const player = this.dashPlayer_();
    if (player) {
      try {
        const videoElement = player.getVideoElement();
        videoElement.volume = volumeLevel;
      } catch (e) {
        this.logger_.error(UVP_PLAYER_ADAPTER_ERROR, e);
      }
    }
  }

  public play(): Promise<void> {
    const player = this.dashPlayer_();
    if (player) {
      player.play();
    } else {
      this.uvpPlayer_.play(true);
    }
    return Promise.resolve();
  }

  public pause(): void {
    const player = this.dashPlayer_();
    if (player) {
      player.pause();
    } else {
      this.uvpPlayer_.pause(true);
    }
  }

  public seek(seekPosition: number): void {
    if (this.isLive && this.uvpPlayer_.adapter.player) {
      const duration = this.uvpPlayer_.adapter.player.duration();
      const seekDelta = Math.min(Math.max(0, seekPosition - this.seekableRange().start), duration);

      this.logger_.log('[UVPPlayerAdapter] [seek live] seekDelta: ', seekDelta, seekPosition);
      this.logger_.log('[UVPPlayerAdapter] [seek live] duration: ', duration);
      this.logger_.log(
        '[UVPPlayerAdapter] [seek live] range: ',
        `${this.seekableRange().start}, ${this.seekableRange().end})`,
      );

      this.uvpPlayer_.adapter.player.seek(seekDelta);
    } else {
      this.uvpPlayer_.seek(seekPosition);
    }
  }

  public stop(): void {
    this.uvpPlayer_.stop();
  }

  public load(initialPlaybackTime?: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const contentUrl = this.processRequestInterceptor(RequestType.MANIFEST, this.contentUrl_);
      const licenseUrl = this.processRequestInterceptor(RequestType.LICENSE, this.licenseUrl_);

      this.uvpPlayer_.drm = {
        licenseRequest: licenseUrl || '',
        licenseRelease: licenseUrl || '',
        distinctiveIdentifier: 'required',
        persistentState: 'required',
        sessionTypes: ['temporary'],
        esvm: false,
      };
      this.uvpPlayer_.src = contentUrl;

      const loadFailed = (): void => {
        reject();
      };

      this.uvpPlayer_.once('canplay' as SceUvp.PlayerEvent.canplay, () => {
        this.uvpPlayer_.off('error' as SceUvp.PlayerEvent.error, loadFailed);

        this.setupDashPlayer_();

        if (initialPlaybackTime) {
          this.seek(initialPlaybackTime);
        }

        resolve();
      });

      this.uvpPlayer_.once('error' as SceUvp.PlayerEvent.error, loadFailed);
    });
  }

  public async getThumbnail(_position: number): Promise<ThumbnailPreviewMetadata | undefined> {
    return undefined;
  }

  public unload(): Promise<void> {
    this.uvpPlayer_.unload();
    this.teardownUvpPlayer_();
    return Promise.resolve();
  }

  public getAllTracks(): TrackInfo[] {
    return this.allTracksInfo_;
  }

  public getActiveTracks(): TrackInfo[] {
    return this.activeTracks_;
  }

  public getVariantTracks(): TrackVariantInfo[] {
    return [];
  }

  public getAvailableTrackVariants(_trackType: TrackType): TrackVariantInfo[] {
    return [];
  }

  public selectTrack(track?: TrackInfo): void {
    if (!track && this.uvpPlayer_) {
      this.uvpPlayer_.subtitles = false;
    }
    if (track && this.uvpPlayer_) {
      if (track.type === TrackType.AUDIO) {
        const trackIndex = this.uvpPlayer_.audioTracks.findIndex(audioTrack => audioTrack.lang === track.language);
        this.uvpPlayer_.audio = trackIndex;
      }
      if (track.type === TrackType.TEXT) {
        const trackIndex = this.uvpPlayer_.subtitleTracks.findIndex(
          subtitleTrack => subtitleTrack.lang === track.language,
        );
        this.uvpPlayer_.subtitles = trackIndex === -1 ? false : trackIndex;
      }
    }
  }

  public subscribe(eventName: string, callback: AnyFunc): void {
    this.emitter_.subscribe(eventName, callback);
  }

  public unsubscribe(eventName: string, callback: AnyFunc): void {
    this.emitter_.unsubscribe(eventName, callback);
  }

  public get playbackStatistics(): PlaybackStatistics {
    const dashPlayer = this.dashPlayer_();
    const bufferedAhead = dashPlayer ? this.getBufferedAhead(dashPlayer, 'video') : undefined;
    return Object.freeze({
      bufferRange: bufferedAhead === undefined ? 0 : bufferedAhead.toString(),
      bufferedAhead,
      decodedFrames: 0,
      droppedFrames: dashPlayer ? this.getDroppedFrame(dashPlayer) : 0,
      corruptedFrames: 0,
      playTime: 0,
      pauseTime: 0,
      licenseTime: 0,
      bufferingTime: 0,
      requiredBandwidth: Math.round(0),
      estimatedBandwidth: Math.round(0),
      variantBitrate: dashPlayer ? this.getBitListInfo(dashPlayer, 'video').bitrate : 0,
      videoBitrate: dashPlayer ? this.getBitListInfo(dashPlayer, 'video').bitrate : 0,
      audioBitrate: dashPlayer ? this.getBitListInfo(dashPlayer, 'audio').bitrate : 0,
      loadLatency: 0,
      width: dashPlayer ? this.getBitListInfo(dashPlayer, 'video').width : 0,
      height: dashPlayer ? this.getBitListInfo(dashPlayer, 'video').height : 0,
      framerate: 0,
    }) as PlaybackStatistics;
  }

  public setRequestInterceptor(interceptor: (requestType: RequestType, request: any) => void): void {
    this.requestInterceptor_ = interceptor;
  }

  public setResponseInterceptor(_: (requestType: RequestType, response: any) => void): void {
    // no-op
  }

  public restrictQuality(_enableRestriction: boolean): void {
    //no-op
  }

  availableMimeTypes(_trackType: TrackType): Array<string> {
    return [];
  }

  setPreferredMimeTypes(_trackType: TrackType, _mimeTypes: Array<string>): void {
    // no -op
  }

  public seekToLiveEdge(): void {
    if (this.isLive) {
      this.seek(this.getLiveEdge());
    }
  }

  public getLiveEdge(): number {
    const duration = 0;
    if (this.isLive) {
      return this.seekableRange().end;
    }
    return duration;
  }

  public seekableRange(): SeekRangeInfo {
    if (!this.isLive) {
      return {
        start: 0,
        end: this.uvpPlayer_.duration,
      };
    }
    // UVP does not expose APIs to extract DVR info for Live streams,
    // so we re using un-documented APIs to extract this info.
    const player = this.dashPlayer_();
    if (player) {
      return player.getDashMetrics().getCurrentDVRInfo().range;
    }
    return {
      start: 0,
      end: 0,
    };
  }

  public currentOffsetFromLiveEdge(): number {
    let offset = 0;
    const player = this.dashPlayer_();
    if (this.isLive && player) {
      try {
        const videoElement = player.getVideoElement();
        offset = this.getLiveEdge() - videoElement.currentTime;
      } catch (e) {
        this.logger_.error(UVP_PLAYER_ADAPTER_ERROR, e);
      }
    }
    return offset;
  }

  public setPlaybackOptions(playbackOptions: PlaybackOptions): void {
    this.playbackOptions_ = playbackOptions;
  }

  unloadAndSavePreload(): Promise<void> {
    // no-op
    return Promise.resolve();
  }

  restoreFromPreload(): void {
    // no-op
  }
}
